import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { stripePromise, createPaymentIntent, confirmPayment } from '../../../services/stripeService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PaymentForm = ({ clientSecret, onSuccess, onError, total }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/downloads`,
        },
        redirect: 'if_required',
      });

      if (error) {
        onError(error?.message);
      } else if (paymentIntent?.status === 'succeeded') {
        const { error: confirmError } = await confirmPayment(paymentIntent?.id);
        if (confirmError) {
          onError('Payment succeeded but confirmation failed. Please contact support.');
        } else {
          onSuccess(paymentIntent);
        }
      }
    } catch (error) {
      onError('An error occurred during payment processing.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-primary/10 rounded-md p-4 flex items-center justify-between mb-4">
        <span className="text-sm md:text-base font-medium text-foreground">
          Order Total
        </span>
        <span className="text-xl md:text-2xl font-heading font-semibold text-primary data-text">
          ${total?.toFixed(2)}
        </span>
      </div>

      <PaymentElement />

      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        disabled={!stripe || isProcessing}
        iconName={isProcessing ? 'Loader2' : 'CreditCard'}
        iconPosition="left"
      >
        {isProcessing ? 'Processing...' : `Pay $${total?.toFixed(2)}`}
      </Button>
    </form>
  );
};

const CheckoutModal = ({ isOpen, onClose, total, onComplete }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState(user ? 'existing' : 'guest');
  const [formData, setFormData] = useState({
    email: user?.email || '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    country: '',
    city: '',
    state: '',
    addressLine1: '',
    zipCode: '',
    agreeTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);

  const countryOptions = [
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'ca', label: 'Canada' },
    { value: 'au', label: 'Australia' },
    { value: 'de', label: 'Germany' },
    { value: 'fr', label: 'France' },
    { value: 'in', label: 'India' },
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (accountType === 'create') {
      if (!formData?.password) {
        newErrors.password = 'Password is required';
      } else if (formData?.password?.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!formData?.firstName) newErrors.firstName = 'First name is required';
    if (!formData?.lastName) newErrors.lastName = 'Last name is required';
    if (!formData?.addressLine1) newErrors.addressLine1 = 'Address is required';
    if (!formData?.city) newErrors.city = 'City is required';
    if (!formData?.state) newErrors.state = 'State is required';
    if (!formData?.country) newErrors.country = 'Country is required';
    if (!formData?.zipCode) newErrors.zipCode = 'ZIP code is required';
    if (!formData?.agreeTerms) newErrors.agreeTerms = 'You must agree to terms';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = async () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      await initializePayment();
    }
  };

  const initializePayment = async () => {
    setIsProcessing(true);
    try {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const ebookId = cart?.[0]?.id;

      let userProfile = null;
      if (user) {
        const { data } = await supabase?.from('user_profiles')?.select('stripe_customer_id')?.eq('id', user?.id)?.single();
        userProfile = data;
      }

      const paymentData = {
        amount: total,
        currency: 'usd',
        ebookId,
        description: `eBook Purchase - ${cart?.[0]?.title || 'StrategyShelf eBook'}`,
      };

      const customerInfo = {
        userId: user?.id || null,
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        email: formData?.email,
        stripeCustomerId: userProfile?.stripe_customer_id || null,
        billing: {
          address_line_1: formData?.addressLine1,
          city: formData?.city,
          state: formData?.state,
          postal_code: formData?.zipCode,
          country: formData?.country,
        },
      };

      const { data, error } = await createPaymentIntent(paymentData, customerInfo);

      if (error) {
        setErrors({ general: 'Failed to initialize payment. Please try again.' });
        return;
      }

      setClientSecret(data?.clientSecret);
      setStripeCustomerId(data?.customerId);
      setStep(3);
    } catch (error) {
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentSuccess = () => {
    onComplete();
  };

  const handlePaymentError = (errorMessage) => {
    setErrors({ general: errorMessage });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-500" onClick={onClose} />
      <div className="fixed inset-0 z-600 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-card rounded-lg shadow-2xl w-full max-w-2xl my-8">
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
            <div className="flex-1">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                Secure Checkout
              </h2>
              <div className="flex items-center gap-2 mt-2">
                <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
                  <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    1
                  </div>
                  <span className="text-xs md:text-sm font-medium hidden sm:inline">Account</span>
                </div>
                <div className="w-8 md:w-12 h-px bg-border" />
                <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
                  <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    2
                  </div>
                  <span className="text-xs md:text-sm font-medium hidden sm:inline">Billing</span>
                </div>
                <div className="w-8 md:w-12 h-px bg-border" />
                <div className={`flex items-center gap-2 ${step >= 3 ? 'text-primary' : 'text-muted-foreground'}`}>
                  <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium ${step >= 3 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    3
                  </div>
                  <span className="text-xs md:text-sm font-medium hidden sm:inline">Payment</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-muted transition-smooth focus-ring"
              aria-label="Close checkout"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {errors?.general && (
              <div className="bg-error/10 border border-error/20 rounded-md p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-error">{errors?.general}</p>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4 md:space-y-6">
                {!user && (
                  <div className="flex gap-2 md:gap-4">
                    <Button
                      variant={accountType === 'guest' ? 'default' : 'outline'}
                      fullWidth
                      onClick={() => setAccountType('guest')}
                    >
                      Guest Checkout
                    </Button>
                    <Button
                      variant={accountType === 'create' ? 'default' : 'outline'}
                      fullWidth
                      onClick={() => setAccountType('create')}
                    >
                      Create Account
                    </Button>
                  </div>
                )}

                <Input
                  type="email"
                  label="Email Address"
                  placeholder="your.email@example.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  required
                  disabled={!!user}
                />

                {accountType === 'create' && !user && (
                  <>
                    <Input
                      type="password"
                      label="Password"
                      placeholder="Minimum 8 characters"
                      value={formData?.password}
                      onChange={(e) => handleInputChange('password', e?.target?.value)}
                      error={errors?.password}
                      required
                    />

                    <Input
                      type="password"
                      label="Confirm Password"
                      placeholder="Re-enter password"
                      value={formData?.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                      error={errors?.confirmPassword}
                      required
                    />
                  </>
                )}

                <div className="bg-muted/50 rounded-md p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Icon name="Shield" size={16} className="text-success" />
                    <span className="font-medium">Your information is secure</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    We use industry-standard encryption to protect your data
                  </p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    label="First Name"
                    placeholder="John"
                    value={formData?.firstName}
                    onChange={(e) => handleInputChange('firstName', e?.target?.value)}
                    error={errors?.firstName}
                    required
                  />

                  <Input
                    type="text"
                    label="Last Name"
                    placeholder="Doe"
                    value={formData?.lastName}
                    onChange={(e) => handleInputChange('lastName', e?.target?.value)}
                    error={errors?.lastName}
                    required
                  />
                </div>

                <Input
                  type="text"
                  label="Address Line 1"
                  placeholder="123 Main Street"
                  value={formData?.addressLine1}
                  onChange={(e) => handleInputChange('addressLine1', e?.target?.value)}
                  error={errors?.addressLine1}
                  required
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    label="City"
                    placeholder="New York"
                    value={formData?.city}
                    onChange={(e) => handleInputChange('city', e?.target?.value)}
                    error={errors?.city}
                    required
                  />

                  <Input
                    type="text"
                    label="State/Province"
                    placeholder="NY"
                    value={formData?.state}
                    onChange={(e) => handleInputChange('state', e?.target?.value)}
                    error={errors?.state}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Select
                    label="Country"
                    value={formData?.country}
                    onChange={(e) => handleInputChange('country', e?.target?.value)}
                    options={countryOptions}
                    error={errors?.country}
                    required
                  />

                  <Input
                    type="text"
                    label="ZIP/Postal Code"
                    placeholder="10001"
                    value={formData?.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
                    error={errors?.zipCode}
                    required
                  />
                </div>

                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={formData?.agreeTerms}
                    onCheckedChange={(checked) => handleInputChange('agreeTerms', checked)}
                  />
                  <label className="text-sm text-muted-foreground">
                    I agree to the terms and conditions and privacy policy
                  </label>
                </div>
                {errors?.agreeTerms && (
                  <p className="text-sm text-error">{errors?.agreeTerms}</p>
                )}
              </div>
            )}

            {step === 3 && clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <PaymentForm
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  total={total}
                />
              </Elements>
            )}
          </div>

          {step < 3 && (
            <div className="p-4 md:p-6 border-t border-border flex gap-3">
              {step > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  disabled={isProcessing}
                >
                  Back
                </Button>
              )}
              <Button
                variant="default"
                fullWidth
                onClick={handleNext}
                disabled={isProcessing}
                iconName={isProcessing ? 'Loader2' : 'ArrowRight'}
                iconPosition="right"
              >
                {isProcessing ? 'Processing...' : step === 2 ? 'Continue to Payment' : 'Continue'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CheckoutModal;